import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { 
  Book, 
  Save, 
  RefreshCw, 
  Plus, 
  X, 
  Search,
  Languages,
  Edit2,
  Trash2,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { getGlossary, updateGlossary } from '../../../services/api';

const LANGUAGE_NAMES = {
  en: 'English',
  pl: 'Polish',
  es: 'Spanish',
  ar: 'Arabic',
  fr: 'French'
};

const LANGUAGE_FLAGS = {
  en: '🇬🇧',
  pl: '🇵🇱',
  es: '🇪🇸',
  ar: '🇸🇦',
  fr: '🇫🇷'
};

const GlossaryManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [glossary, setGlossary] = useState(null);
  const [activeTab, setActiveTab] = useState('en');
  const [searchQuery, setSearchQuery] = useState('');
  
  // New term form
  const [newSwedishTerm, setNewSwedishTerm] = useState('');
  const [newTranslations, setNewTranslations] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Edit mode
  const [editingTerm, setEditingTerm] = useState(null);
  const [editTranslation, setEditTranslation] = useState('');

  const fetchGlossary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getGlossary();
      setGlossary(data);
    } catch (error) {
      console.error('Error fetching glossary:', error);
      toast.error('Kunde inte ladda ordlistan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGlossary();
  }, [fetchGlossary]);

  // Get all Swedish terms (source terms)
  const getSwedishTerms = () => {
    if (!glossary?.sv?.en) return [];
    return Object.keys(glossary.sv.en).sort((a, b) => 
      a.toLowerCase().localeCompare(b.toLowerCase(), 'sv')
    );
  };

  // Filter terms based on search
  const filteredTerms = getSwedishTerms().filter(term => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    // Search in Swedish term
    if (term.toLowerCase().includes(query)) return true;
    // Search in translations
    for (const lang of Object.keys(glossary.sv)) {
      if (glossary.sv[lang][term]?.toLowerCase().includes(query)) return true;
    }
    return false;
  });

  // Handle editing a translation
  const startEditing = (term, lang) => {
    setEditingTerm({ term, lang });
    setEditTranslation(glossary.sv[lang]?.[term] || '');
  };

  const cancelEditing = () => {
    setEditingTerm(null);
    setEditTranslation('');
  };

  const saveEdit = async () => {
    if (!editingTerm) return;
    
    const { term, lang } = editingTerm;
    const updatedGlossary = { ...glossary };
    
    if (!updatedGlossary.sv[lang]) {
      updatedGlossary.sv[lang] = {};
    }
    updatedGlossary.sv[lang][term] = editTranslation;
    
    setSaving(true);
    try {
      await updateGlossary(updatedGlossary);
      setGlossary(updatedGlossary);
      toast.success('Översättning uppdaterad');
      cancelEditing();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  // Handle adding a new term
  const addNewTerm = async () => {
    if (!newSwedishTerm.trim()) {
      toast.error('Ange ett svenskt ord');
      return;
    }

    // Check if term already exists
    if (glossary.sv.en[newSwedishTerm.toLowerCase()]) {
      toast.error('Termen finns redan');
      return;
    }

    const updatedGlossary = { ...glossary };
    
    // Add the term to all languages
    for (const lang of Object.keys(LANGUAGE_NAMES)) {
      if (!updatedGlossary.sv[lang]) {
        updatedGlossary.sv[lang] = {};
      }
      if (newTranslations[lang]?.trim()) {
        updatedGlossary.sv[lang][newSwedishTerm] = newTranslations[lang];
      }
    }

    setSaving(true);
    try {
      await updateGlossary(updatedGlossary);
      setGlossary(updatedGlossary);
      toast.success('Ny term tillagd');
      setNewSwedishTerm('');
      setNewTranslations({});
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding term:', error);
      toast.error('Kunde inte lägga till termen');
    } finally {
      setSaving(false);
    }
  };

  // Handle deleting a term
  const deleteTerm = async (term) => {
    if (!window.confirm(`Vill du verkligen ta bort "${term}" från ordlistan?`)) {
      return;
    }

    const updatedGlossary = { ...glossary };
    
    // Remove from all languages
    for (const lang of Object.keys(updatedGlossary.sv)) {
      delete updatedGlossary.sv[lang][term];
    }

    setSaving(true);
    try {
      await updateGlossary(updatedGlossary);
      setGlossary(updatedGlossary);
      toast.success('Term borttagen');
    } catch (error) {
      console.error('Error deleting term:', error);
      toast.error('Kunde inte ta bort termen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const availableLanguages = glossary?.sv ? Object.keys(glossary.sv).filter(l => l !== 'sv') : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Katolsk Ordlista</h2>
          <p className="text-gray-600">Hantera katolska termer och deras översättningar</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={fetchGlossary}
            variant="outline"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Uppdatera
          </Button>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Lägg till term
          </Button>
        </div>
      </div>

      {/* Add New Term Form */}
      {showAddForm && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <Plus className="w-5 h-5" />
              Lägg till ny term
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Svenskt ord (källterm)
              </label>
              <Input
                value={newSwedishTerm}
                onChange={(e) => setNewSwedishTerm(e.target.value)}
                placeholder="t.ex. tabernaklet"
                className="max-w-sm"
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Object.entries(LANGUAGE_NAMES).map(([lang, name]) => (
                <div key={lang}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {LANGUAGE_FLAGS[lang]} {name}
                  </label>
                  <Input
                    value={newTranslations[lang] || ''}
                    onChange={(e) => setNewTranslations({ ...newTranslations, [lang]: e.target.value })}
                    placeholder={`${name}...`}
                    dir={lang === 'ar' ? 'rtl' : 'ltr'}
                  />
                </div>
              ))}
            </div>
            
            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={addNewTerm}
                disabled={saving || !newSwedishTerm.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Spara term
              </Button>
              <Button
                onClick={() => {
                  setShowAddForm(false);
                  setNewSwedishTerm('');
                  setNewTranslations({});
                }}
                variant="outline"
              >
                Avbryt
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Sök termer..."
          className="pl-10"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Language Tabs */}
      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="flex items-center gap-2">
            <Book className="w-5 h-5 text-purple-600" />
            Översättningar ({filteredTerms.length} termer)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              {availableLanguages.map((lang) => (
                <TabsTrigger key={lang} value={lang} className="flex items-center gap-2">
                  <span>{LANGUAGE_FLAGS[lang]}</span>
                  <span className="hidden sm:inline">{LANGUAGE_NAMES[lang]}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {availableLanguages.map((lang) => (
              <TabsContent key={lang} value={lang}>
                <div className="border rounded-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-4 p-3 bg-gray-100 font-medium text-sm text-gray-700 border-b">
                    <div className="col-span-4">Svenska (källa)</div>
                    <div className="col-span-6">{LANGUAGE_NAMES[lang]} (översättning)</div>
                    <div className="col-span-2 text-right">Åtgärder</div>
                  </div>
                  
                  {/* Table Body */}
                  <div className="divide-y max-h-[500px] overflow-y-auto">
                    {filteredTerms.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        {searchQuery ? 'Inga termer matchade sökningen' : 'Inga termer i ordlistan'}
                      </div>
                    ) : (
                      filteredTerms.map((term) => (
                        <div 
                          key={term} 
                          className="grid grid-cols-12 gap-4 p-3 items-center hover:bg-gray-50"
                        >
                          <div className="col-span-4">
                            <span className="font-medium text-gray-900">{term}</span>
                          </div>
                          <div className="col-span-6" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                            {editingTerm?.term === term && editingTerm?.lang === lang ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  value={editTranslation}
                                  onChange={(e) => setEditTranslation(e.target.value)}
                                  className="flex-1"
                                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveEdit();
                                    if (e.key === 'Escape') cancelEditing();
                                  }}
                                />
                                <Button
                                  size="sm"
                                  onClick={saveEdit}
                                  disabled={saving}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEditing}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <span className={glossary.sv[lang]?.[term] ? 'text-gray-700' : 'text-gray-400 italic'}>
                                {glossary.sv[lang]?.[term] || '(saknas)'}
                              </span>
                            )}
                          </div>
                          <div className="col-span-2 flex items-center justify-end gap-2">
                            {editingTerm?.term !== term && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => startEditing(term, lang)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteTerm(term)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Languages className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Om ordlistan</h4>
              <p className="text-sm text-blue-700">
                Ordlistan innehåller katolska termer som används för att säkerställa korrekta översättningar 
                av religiöst innehåll. När innehåll översätts automatiskt används dessa termer för att 
                ersätta generiska översättningar med de korrekta katolska termerna.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GlossaryManager;
